import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Alert,
  Chip,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import api from '../../utils/api';

const POLL_INTERVAL_MS = 3000;

const formatDateTime = (ms) => {
  if (!ms) return 'N/A';
  try {
    return new Date(ms).toLocaleString();
  } catch (e) {
    return 'N/A';
  }
};

const CustomerSegmentation = () => {
  const { actions } = useApp();

  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [meta, setMeta] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(false);

  const pollRef = useRef(null);

  const fetchMeta = useCallback(async () => {
    try {
      const res = await api.get('/customer-segmentation/meta');
      setMeta(res.data?.data || null);
      return res.data?.data || null;
    } catch (e) {
      console.error('Failed to load segmentation status:', e);
      return null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      const latest = await fetchMeta();
      if (!latest || latest.status !== 'processing') {
        stopPolling();
      }
    }, POLL_INTERVAL_MS);
  }, [fetchMeta, stopPolling]);

  useEffect(() => {
    (async () => {
      setLoadingMeta(true);
      const latest = await fetchMeta();
      setLoadingMeta(false);
      if (latest?.status === 'processing') startPolling();
    })();
    return () => stopPolling();
  }, [fetchMeta, startPolling, stopPolling]);

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/customer-segmentation/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'customer_segmentation_template.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Template download failed:', e);
      actions.showSnackbar('Failed to download template', 'error');
    }
  };

  const handleFileSelect = async (file) => {
    if (!file) return;
    if (!file.name?.toLowerCase().endsWith('.xlsx')) {
      actions.showSnackbar('Only .xlsx files are supported', 'warning');
      return;
    }
    setSelectedFile(file);
    setPreview(null);
    setPreviewing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/customer-segmentation/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data?.success) {
        setPreview(res.data.data);
        actions.showSnackbar(res.data.message || 'File parsed', 'success');
      } else {
        actions.showSnackbar(res.data?.message || 'Failed to parse file', 'error');
      }
    } catch (e) {
      console.error('Preview failed:', e);
      actions.showSnackbar(e.response?.data?.message || 'Failed to parse file', 'error');
      setSelectedFile(null);
    } finally {
      setPreviewing(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await api.post('/customer-segmentation/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data?.success) {
        actions.showSnackbar(res.data.message || 'Upload started', 'success');
        setSelectedFile(null);
        setPreview(null);
        await fetchMeta();
        startPolling();
      } else {
        actions.showSnackbar(res.data?.message || 'Failed to start upload', 'error');
      }
    } catch (e) {
      console.error('Upload failed:', e);
      actions.showSnackbar(e.response?.data?.message || 'Failed to start upload', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleResume = async () => {
    if (!selectedFile) return;
    setResuming(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await api.post('/customer-segmentation/resume', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data?.success) {
        actions.showSnackbar(res.data.message || 'Resuming upload', 'success');
        setSelectedFile(null);
        setPreview(null);
        await fetchMeta();
        startPolling();
      } else {
        actions.showSnackbar(res.data?.message || 'Failed to resume upload', 'error');
      }
    } catch (e) {
      console.error('Resume failed:', e);
      actions.showSnackbar(e.response?.data?.message || 'Failed to resume upload', 'error');
    } finally {
      setResuming(false);
    }
  };

  const handleCancel = async () => {
    try {
      await api.post('/customer-segmentation/cancel');
      actions.showSnackbar('Cancellation requested', 'info');
      await fetchMeta();
    } catch (e) {
      actions.showSnackbar(e.response?.data?.message || 'Failed to cancel', 'error');
    }
  };

  const isProcessing = meta?.status === 'processing';
  const canResume =
    !!meta &&
    (meta.status === 'failed' || meta.status === 'cancelled') &&
    (meta.lastProcessedRow || 0) > 0;
  const progressPct =
    meta && meta.total ? Math.min(100, Math.round((meta.processed / meta.total) * 100)) : 0;

  const statusColor = {
    processing: 'warning',
    done: 'success',
    failed: 'error',
    cancelled: 'default'
  }[meta?.status] || 'default';

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
        Customer Segmentation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload the marketing customer file with <b>MobileNo</b> and <b>category</b> columns.
        Only existing customers are updated; numbers not in the database are skipped.
        Use the template below for the correct format.
      </Typography>

      {/* Upload card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">Upload new file</Typography>
          <Button
            variant="text"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download template
          </Button>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
            disabled={previewing || uploading || isProcessing}
          >
            {previewing ? 'Parsing...' : 'Choose .xlsx'}
            <input
              type="file"
              hidden
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => {
                const file = e.target.files?.[0];
                handleFileSelect(file);
                e.target.value = '';
              }}
            />
          </Button>
          <Typography variant="body2" color="text.secondary">
            {selectedFile?.name || 'No file selected'}
          </Typography>
        </Stack>

        {isProcessing && (
          <Alert severity="info" sx={{ mt: 2 }}>
            An upload is currently in progress. Please wait for it to finish before starting a new one.
          </Alert>
        )}

        {canResume && !isProcessing && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            The last upload stopped at row {meta.lastProcessedRow} of {meta.total}. Re-select the{' '}
            <b>same file</b> to resume from there, or start a fresh upload to run from the beginning.
          </Alert>
        )}

        {preview && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Preview ({preview.sheet})
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Chip label={`Total: ${preview.total}`} />
              <Chip label={`Valid: ${preview.valid}`} color="success" variant="outlined" />
              <Chip label={`Invalid: ${preview.invalid}`} color="error" variant="outlined" />
              <Chip label={`VIP Star: ${preview.byTier?.STAR ?? 0}`} color="success" variant="outlined" />
              <Chip label={`VIP: ${preview.byTier?.VIP ?? 0}`} color="warning" variant="outlined" />
              <Chip label={`Regular: ${preview.byTier?.REG ?? 0}`} color="info" variant="outlined" />
              <Chip label={`New: ${preview.byTier?.NEW ?? 0}`} variant="outlined" />
              {preview.byTier?.OTHER > 0 && (
                <Chip label={`Other: ${preview.byTier.OTHER}`} variant="outlined" />
              )}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={uploading || resuming || isProcessing || preview.valid === 0}
                startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
              >
                {uploading ? 'Starting...' : `Update ${preview.valid} customers`}
              </Button>
              {canResume && (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleResume}
                  disabled={uploading || resuming || isProcessing}
                  startIcon={resuming ? <CircularProgress size={16} /> : <RefreshIcon />}
                >
                  {resuming ? 'Resuming...' : `Resume from row ${meta.lastProcessedRow}`}
                </Button>
              )}
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Status card */}
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">Last upload status</Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={fetchMeta} disabled={loadingMeta}>
            Refresh
          </Button>
        </Stack>

        {loadingMeta && !meta ? (
          <CircularProgress size={24} />
        ) : !meta ? (
          <Typography variant="body2" color="text.secondary">
            No uploads yet.
          </Typography>
        ) : (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Chip label={meta.status} color={statusColor} size="small" />
              {isProcessing && (
                <Button size="small" color="error" onClick={handleCancel}>
                  Cancel
                </Button>
              )}
            </Stack>

            {(isProcessing || meta.status === 'done') && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressPct}
                  sx={{ height: 8, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {meta.processed || 0} / {meta.total || 0} rows ({progressPct}%)
                </Typography>
              </Box>
            )}

            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Sheet</TableCell>
                  <TableCell align="right">{meta.sheet || 'N/A'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Updated (existing customers)</TableCell>
                  <TableCell align="right">{meta.updated || 0}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Skipped (not in database)</TableCell>
                  <TableCell align="right">{meta.skipped || 0}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Invalid rows</TableCell>
                  <TableCell align="right">{meta.invalid || 0}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Failed writes</TableCell>
                  <TableCell align="right">{meta.failed || 0}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Uploaded by</TableCell>
                  <TableCell align="right">{meta.uploader || 'N/A'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Started</TableCell>
                  <TableCell align="right">{formatDateTime(meta.startedAt)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Finished</TableCell>
                  <TableCell align="right">{formatDateTime(meta.finishedAt)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {meta.status === 'failed' && meta.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {meta.error}
              </Alert>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default CustomerSegmentation;
